/**
 * Pure display rules for profile change requests (#281, Contract dnm-work#142). Unknown sections,
 * fields and statuses always render: a request is never hidden because the API grew a value.
 */
import { actionFailureMessage } from '../../api/actionFailure';
import { CHANGE_REQUEST_ERROR_CODES, DnmRequestFailure } from '../../api/dnmFailure';
import type {
  AdminUserResponse,
  ChangeRequestErrorCode,
  ChangeRequestSection,
  ChangeRequestStatus,
  DriverProfileResponse,
  FleetManagerProfileResponse,
  ProfileChangeRequest,
  ShipperProfileResponse,
  UserRole,
} from '../../api/types';
import type { StatusIconName } from '../ui/StatusIcon';

/** The KYC tab section a card sits under (KycReviewContent `SectionId`). */
export type ChangeRequestHost = 'personal' | 'licence' | 'business' | 'pan' | 'banking';

function titleCase(value: string): string {
  const words = value.replace(/_/g, ' ').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

const SECTION_LABELS: Record<ChangeRequestSection, string> = {
  PERSONAL: 'Personal',
  LICENCE: 'Licence',
  BUSINESS: 'Business',
  TAX: 'Tax & documents',
  BANKING: 'Bank details',
};

export function sectionLabel(section: string | undefined): string {
  if (!section) return 'Profile';
  return SECTION_LABELS[section as ChangeRequestSection] ?? titleCase(section);
}

/** TAX covers PAN and GST: its one card sits under PAN details, the first of the two tax cards. */
const SECTION_HOSTS: Record<ChangeRequestSection, { host: ChangeRequestHost; roles: UserRole[] }> =
  {
    PERSONAL: { host: 'personal', roles: ['DRIVER', 'SHIPPER', 'FLEET_MANAGER'] },
    LICENCE: { host: 'licence', roles: ['DRIVER'] },
    BUSINESS: { host: 'business', roles: ['SHIPPER', 'FLEET_MANAGER'] },
    TAX: { host: 'pan', roles: ['SHIPPER', 'FLEET_MANAGER'] },
    BANKING: { host: 'banking', roles: ['FLEET_MANAGER'] },
  };

/** null → the fallback "Change requests" card at the top of the tab. */
export function sectionHost(section: string | undefined, role: string): ChangeRequestHost | null {
  const known = section ? SECTION_HOSTS[section as ChangeRequestSection] : undefined;
  return known && known.roles.includes(role as UserRole) ? known.host : null;
}

/** Same words as the KYC tab's own field labels. */
const FIELD_LABELS: Record<string, string> = {
  fullName: 'Name',
  licenseNumber: 'DL number',
  licenseExpiry: 'DL expiry',
  dlDocumentUrl: 'Driving licence document',
  companyName: 'Business name',
  gstin: 'GST number',
  panNumber: 'PAN number',
  gstDocumentUrl: 'GST certificate',
  panDocumentUrl: 'PAN card',
  bankName: 'Bank name',
  accountHolderName: 'Account holder name',
  accountNumber: 'Account number',
  ifscCode: 'IFSC code',
  bankProofType: 'Bank proof type',
  bankProofUrl: 'Bank proof',
};

export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key;
}

export function isDocumentField(key: string): boolean {
  return key.endsWith('DocumentUrl') || key === 'bankProofUrl';
}

const SECRET_FIELDS = new Set(['accountNumber']);

/** The Contract's request keys, read from the user and profile the KYC tab already has. */
const PROFILE_KEYS = [
  'licenseNumber',
  'licenseExpiry',
  'dlDocumentUrl',
  'companyName',
  'gstin',
  'panNumber',
  'gstDocumentUrl',
  'panDocumentUrl',
  'bankName',
  'accountHolderName',
  'accountNumber',
  'ifscCode',
  'bankProofType',
  'bankProofUrl',
] as const;

export type ProfileValues = Record<string, string | null | undefined>;

export function currentProfileValues(
  user: Pick<AdminUserResponse, 'fullName'>,
  profile: DriverProfileResponse | FleetManagerProfileResponse | ShipperProfileResponse | null,
): ProfileValues {
  const values: ProfileValues = { fullName: user.fullName };
  if (!profile) return values;
  const source = profile as unknown as Record<string, unknown>;
  for (const key of PROFILE_KEYS) {
    const v = source[key];
    if (typeof v === 'string') values[key] = v;
    else if (v === null) values[key] = null;
  }
  return values;
}

export interface DiffRow {
  key: string;
  label: string;
  /** The current profile value (what approving overwrites). */
  now: string | null;
  /** `before[key]`, only when it differs from `now` (the profile changed after the user asked). */
  whenAsked: string | null;
  requested: string;
  isDocument: boolean;
  isSecret: boolean;
}

const blankToNull = (v: string | null | undefined) => (v == null || v === '' ? null : v);

/** Only the keys whose requested value differs from `before`. */
export function diffRows(request: ProfileChangeRequest, current: ProfileValues): DiffRow[] {
  const changes = request.changes ?? {};
  const before = request.before ?? {};
  return Object.entries(changes)
    .filter(([key, requested]) => blankToNull(requested) !== blankToNull(before[key]))
    .map(([key, requested]) => {
      const now = blankToNull(current[key]);
      const asked = blankToNull(before[key]);
      return {
        key,
        label: fieldLabel(key),
        now,
        whenAsked: asked !== now ? asked : null,
        requested,
        isDocument: isDocumentField(key),
        isSecret: SECRET_FIELDS.has(key),
      };
    });
}

const STATUS_DISPLAY: Record<
  ChangeRequestStatus,
  { label: string; icon: StatusIconName; tone: 'amber' | 'green' | 'red' }
> = {
  PENDING: { label: 'Pending', icon: 'hourglass', tone: 'amber' },
  APPROVED: { label: 'Approved', icon: 'check', tone: 'green' },
  REJECTED: { label: 'Rejected', icon: 'close', tone: 'red' },
};

/** Pill words and icons from the shared SVG set (R14); unknown → grey with the word title-cased. */
export function statusDisplay(status: string | undefined): {
  label: string;
  icon: StatusIconName | undefined;
  bg: string;
  text: string;
} {
  const known = status ? STATUS_DISPLAY[status as ChangeRequestStatus] : undefined;
  const tone = known?.tone ?? 'gray';
  return {
    label: known?.label ?? (titleCase(status ?? '') || 'Unknown'),
    icon: known?.icon,
    bg: `var(--status-${tone}-bg)`,
    text: `var(--status-${tone}-text)`,
  };
}

/** Last 4 only; never logged. */
export function maskAccount(value: string | null | undefined): string | null {
  if (!value) return null;
  return `•••• ${value.slice(-4)}`;
}

const ERROR_MESSAGES: Record<ChangeRequestErrorCode, string> = {
  CHANGE_REQUEST_NOT_PENDING: 'This request was already decided. Reload to see the result.',
  CHANGE_REQUEST_NOT_FOUND: 'This change request no longer exists. Reload the list.',
  CHANGE_REQUEST_REASON_REQUIRED: 'Give a reason (up to 200 characters).',
};

function changeRequestCode(error: unknown): ChangeRequestErrorCode | undefined {
  if (!(error instanceof DnmRequestFailure)) return undefined;
  return CHANGE_REQUEST_ERROR_CODES.find((c) => c === error.code);
}

/** Local copy only; the API's free-text `error` is never read (same rule as actionFailure.ts). */
export function changeRequestErrorMessage(error: unknown): string {
  const code = changeRequestCode(error);
  return code ? ERROR_MESSAGES[code] : actionFailureMessage(error);
}

/** The request was decided elsewhere or no longer exists: reload the list. */
export function isStaleDecision(error: unknown): boolean {
  const code = changeRequestCode(error);
  return code === 'CHANGE_REQUEST_NOT_PENDING' || code === 'CHANGE_REQUEST_NOT_FOUND';
}
